import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, X, Activity, BarChart3 } from 'lucide-react';

const StandingsTable = ({ standings }) => {
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [forecastGames, setForecastGames] = useState(3);
  const [momentumWeight, setMomentumWeight] = useState(0.7);
  const [showConfidence, setShowConfidence] = useState(true);

  const getDivisions = () => {
    const divisions = {};

    // Flatten the standings array if it's nested
    const flatStandings = Array.isArray(standings[0]) ? standings[0] : standings;

    // Group standings by division/group (works for any league, not just MLB)
    flatStandings.forEach(entry => {
      const division = entry.group?.name || entry.stage || 'League';
      if (!divisions[division]) {
        divisions[division] = [];
      }
      divisions[division].push(entry);
    });
    return divisions;
  };

  const getPositionColor = (position) => {
    if (position <= 1) return 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white';
    if (position <= 3) return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white';
    return 'bg-gray-700 text-gray-300';
  };

  const getStreakIcon = (form) => {
    if (!form || form.length === 0) return null;
    const lastGame = form[form.length - 1];
    if (lastGame === 'W') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (lastGame === 'L') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getFormColor = (form) => {
    if (!form) return '';
    const wins = (form.match(/W/g) || []).length;
    const total = form.length;
    const winRate = wins / total;
    
    if (winRate >= 0.7) return 'text-green-400';
    if (winRate >= 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  const divisions = getDivisions();
  const divisionKeys = Object.keys(divisions);

  // Get teams to display based on selected division
  const teamsToDisplay = useMemo(() => {
    if (selectedDivision === 'all') {
      return Object.entries(divisions);
    }
    return [[selectedDivision, divisions[selectedDivision] || []]];
  }, [selectedDivision, divisions]);

  // Generate sparkline data from form string (WWLWL etc)
  const generateSparklineData = (form) => {
    if (!form) return [];
    
    const points = [];
    let currentValue = 50; // Start at middle
    
    // Convert form string to data points
    form.split('').forEach((result, index) => {
      if (result === 'W') {
        currentValue += 10;
      } else if (result === 'L') {
        currentValue -= 10;
      }
      // Keep within bounds
      currentValue = Math.max(0, Math.min(100, currentValue));
      points.push({ x: index, y: currentValue });
    });
    
    // Add forecast points (3 games ahead)
    const lastValue = points[points.length - 1]?.y || 50;
    const trend = lastValue > 50 ? 5 : -5; // Simplified trend
    
    for (let i = 1; i <= 3; i++) {
      points.push({
        x: form.length + i - 1,
        y: Math.max(0, Math.min(100, lastValue + (trend * i))),
        forecast: true
      });
    }
    
    return points;
  };

  // Create SVG path from points
  const createPath = (points) => {
    if (points.length === 0) return '';
    
    return points.map((point, index) => {
      const x = (point.x / (points.length - 1)) * 200;
      const y = 40 - (point.y / 100) * 40;
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
  };

  if (!standings || standings.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No standings data available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Division Selector */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setSelectedDivision('all')}
          className={`px-4 py-2 rounded-lg transition-all ${
            selectedDivision === 'all'
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
              : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50'
          }`}
        >
          All Divisions
        </button>
        {divisionKeys.map(division => (
          <button
            key={division}
            onClick={() => setSelectedDivision(division)}
            className={`px-4 py-2 rounded-lg transition-all ${
              selectedDivision === division
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50'
            }`}
          >
            {division}
          </button>
        ))}
      </div>

      {teamsToDisplay.map(([division, teams]) => (
        <motion.div
          key={division}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50"
        >
          <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
            {division}
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700/50">
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">#</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-400">Team</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">W</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">L</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">PCT</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">GB</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">Home</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">Away</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">RS</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">RA</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">Diff</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400">Strk</th>
                  <th className="text-center py-3 px-2 text-sm font-medium text-gray-400 hidden md:table-cell">L10</th>
                </tr>
              </thead>
              <tbody>
                {teams.sort((a, b) => a.position - b.position).map((entry, index) => {
                  const team = entry.team;
                  const wins = entry.games?.win?.total || 0;
                  const losses = entry.games?.lose?.total || 0;
                  const winPct = entry.games?.win?.percentage || '0.000';
                  const pointsFor = entry.points?.for || 0;
                  const pointsAgainst = entry.points?.against || 0;
                  const diff = pointsFor - pointsAgainst;
                  
                  return (
                    <motion.tr
                      key={team?.id || `${division}-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-gray-700/30 hover:bg-gray-700/20 transition-colors"
                    >
                      <td className="py-3 px-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${getPositionColor(entry.position)}`}>
                          {entry.position}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div 
                          className="flex items-center gap-3 cursor-pointer hover:text-blue-400 transition-colors"
                          onClick={() => setSelectedTeam({ ...entry, sparklineData: generateSparklineData(entry.form) })}
                        >
                          {team?.logo && (
                            <img 
                              src={team.logo} 
                              alt={team.name}
                              className="w-8 h-8 object-contain"
                            />
                          )}
                          <span className="font-medium">{team?.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="text-center py-3 px-2 text-white font-medium">{wins}</td>
                      <td className="text-center py-3 px-2 text-gray-400">{losses}</td>
                      <td className="text-center py-3 px-2 text-blue-400 font-medium">
                        {winPct}
                      </td>
                      <td className="text-center py-3 px-2 text-gray-400">
                        {entry.games?.behind || '-'}
                      </td>
                      <td className="text-center py-3 px-2 text-gray-400 text-sm">
                        {entry.home ? `${entry.home.win || 0}-${entry.home.lose || 0}` : '-'}
                      </td>
                      <td className="text-center py-3 px-2 text-gray-400 text-sm">
                        {entry.away ? `${entry.away.win || 0}-${entry.away.lose || 0}` : '-'}
                      </td>
                      <td className="text-center py-3 px-2 text-green-400 font-medium">
                        {pointsFor}
                      </td>
                      <td className="text-center py-3 px-2 text-red-400 font-medium">
                        {pointsAgainst}
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className={`font-medium ${
                          diff > 0 ? 'text-green-400' : 
                          diff < 0 ? 'text-red-400' : 
                          'text-gray-400'
                        }`}>
                          {diff > 0 ? '+' : ''}{diff}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <div className="flex items-center justify-center gap-1">
                          {getStreakIcon(entry.form)}
                          <span className="text-sm text-gray-400">
                            {entry.form ? `${entry.form[entry.form.length - 1]}${entry.streak || ''}` : '-'}
                          </span>
                        </div>
                      </td>
                      <td className="text-center py-3 px-2 hidden md:table-cell">
                        <span className={`text-sm font-medium ${getFormColor(entry.form?.slice(-10))}`}>
                          {entry.form ? `${(entry.form.slice(-10).match(/W/g) || []).length}-${(entry.form.slice(-10).match(/L/g) || []).length}` : '-'}
                        </span>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      ))}

      {/* Team Forecast Modal */}
      <AnimatePresence>
        {selectedTeam && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedTeam(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-xl p-6 max-w-lg w-full border border-gray-700"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  {selectedTeam.team?.logo && (
                    <img 
                      src={selectedTeam.team.logo} 
                      alt={selectedTeam.team.name}
                      className="w-10 h-10 object-contain"
                    />
                  )}
                  <h2 className="text-xl font-bold text-white">
                    {selectedTeam.team?.name} Performance Forecast
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedTeam(null)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Current Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-900/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">
                    {selectedTeam.games?.win?.total || 0}-{selectedTeam.games?.lose?.total || 0}
                  </div>
                  <div className="text-xs text-gray-400">Record</div>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-400">
                    {selectedTeam.games?.win?.percentage || '0.000'}
                  </div>
                  <div className="text-xs text-gray-400">Win %</div>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3 text-center">
                  <div className={`text-2xl font-bold ${
                    (selectedTeam.points?.for - selectedTeam.points?.against) > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {(selectedTeam.points?.for - selectedTeam.points?.against) > 0 ? '+' : ''}
                    {selectedTeam.points?.for - selectedTeam.points?.against}
                  </div>
                  <div className="text-xs text-gray-400">Run Diff</div>
                </div>
              </div>

              {/* Sparkline Chart */}
              <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-gray-300">
                    Recent Form & 3-Game Forecast
                  </span>
                </div>
                
                <svg viewBox="0 0 200 40" className="w-full h-20">
                  {/* Grid lines */}
                  <line x1="0" y1="20" x2="200" y2="20" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                  
                  {/* Sparkline */}
                  {selectedTeam.sparklineData && selectedTeam.sparklineData.length > 0 && (
                    <>
                      {/* Historical line */}
                      <path
                        d={createPath(selectedTeam.sparklineData.filter(p => !p.forecast))}
                        fill="none"
                        stroke="rgb(96, 165, 250)"
                        strokeWidth="2"
                      />
                      
                      {/* Forecast line (dashed) */}
                      <path
                        d={createPath(selectedTeam.sparklineData.filter((p, i, arr) => 
                          p.forecast || (i === arr.findIndex(pt => pt.forecast) - 1)
                        ))}
                        fill="none"
                        stroke="rgb(167, 139, 250)"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                      />
                      
                      {/* Data points */}
                      {selectedTeam.sparklineData.map((point, index) => (
                        <circle
                          key={index}
                          cx={(point.x / (selectedTeam.sparklineData.length - 1)) * 200}
                          cy={40 - (point.y / 100) * 40}
                          r="3"
                          fill={point.forecast ? "rgb(167, 139, 250)" : "rgb(96, 165, 250)"}
                        />
                      ))}
                    </>
                  )}
                </svg>
                
                <div className="flex justify-between mt-2 text-xs text-gray-400">
                  <span>Past Games</span>
                  <span className="text-purple-400">Forecast →</span>
                </div>
              </div>

              {/* Last 10 Details */}
              <div className="bg-gray-900/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium text-gray-300">Last 10 Games</span>
                </div>
                <div className="flex gap-1">
                  {selectedTeam.form && selectedTeam.form.slice(-10).split('').map((result, index) => (
                    <div
                      key={index}
                      className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                        result === 'W' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {result}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StandingsTable;